<template>
    <div class="pl-terminal__row">
        <label class="pl-terminal__label" :for="id">{{ label }}</label>

        <textarea
            :id="id"
            ref="field"
            class="pl-textarea"
            rows="5"
            :value="modelValue"
            :placeholder="placeholder"
            :aria-describedby="countId"
            @input="$emit('update:modelValue', $event.target.value)"
        ></textarea>

        <div class="pl-terminal__hint">
            <div class="pl-terminal__field">
                <button
                    v-if="canPaste"
                    type="button"
                    class="pl-btn pl-btn--ghost pl-btn--sm"
                    @click="paste"
                >
                    <i class="fas fa-paste" aria-hidden="true"></i>
                    {{ t('player.creation.identity.paste') }}
                </button>
                <button
                    v-if="modelValue"
                    type="button"
                    class="pl-btn pl-btn--ghost pl-btn--sm"
                    @click="clear"
                >
                    {{ t('player.creation.identity.clear') }}
                </button>
            </div>

            <span :id="countId" class="pl-terminal__count" :class="{ 'is-over': over }">
                {{ length }}/{{ max }}
            </span>
        </div>

        <p v-if="pasteFailed" class="pl-field__error">
            {{ t('player.creation.identity.pasteFailed') }}
        </p>
    </div>
</template>

<script setup>
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';

const props = defineProps({
    id: {type: String, required: true},
    label: {type: String, required: true},
    placeholder: {type: String, default: ''},
    modelValue: {type: String, default: ''},
    max: {type: Number, required: true},
});

const emit = defineEmits(['update:modelValue']);

const {t} = useI18n();

const field = ref(null);
const pasteFailed = ref(false);

const countId = computed(() => `${props.id}-count`);
/* Grapheme-agnostic on purpose: the backend limit is a character count on a
   Django TextField, so this must count the same units the server does. */
const length = computed(() => props.modelValue.length);
const over = computed(() => length.value > props.max);

/* The Clipboard read API needs a secure context and, in Chromium, an explicit
   permission grant. Where it is unavailable the button is not rendered at all
   rather than shown and broken — Ctrl/Cmd-V into the textarea still works, and
   that is the fallback the absence of the button points at. */
const canPaste = computed(
    () => typeof navigator !== 'undefined' && !!navigator.clipboard?.readText,
);

async function paste() {
    pasteFailed.value = false;
    try {
        const text = await navigator.clipboard.readText();
        if (!text) return;
        /* Appends rather than replaces. A player pasting a second paragraph
           into a field that already holds the first should not silently lose
           it; the Clear button is the explicit way to start over. */
        const joined = props.modelValue ? `${props.modelValue}\n\n${text}` : text;
        emit('update:modelValue', joined);
    } catch {
        pasteFailed.value = true;
    }
}

function clear() {
    emit('update:modelValue', '');
    field.value?.focus();
}
</script>
